import Column from '@components/Column';
import CtaLayout from '@components/CtaLayout';
import GiftTypeNotes from '@components/gift/GiftTypeNotes';
import Header from '@components/Header';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import useGiftItem from '@hooks/gift/useGiftItem';
import { GiftItemType } from '@models/gift';
import GiftOrderProvider from '@providers/GiftOrderProvider';
import GiftOrderContext from '@providers/GiftOrderProvider/GiftOrderContext';
import { AppScreen } from '@stackflow/plugin-basic-ui';
import { ActivityComponentType } from '@stackflow/react';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CardInfo from './components/CardInfo';
import Complete from './components/Complete';
import Footer from './components/Footer';
import ItemInfo from './components/ItemInfo';
import Message from './components/Message';
import OrderAgreements from './components/OrderAgreements';
import PaymentInfo from './components/PaymentInfo';
import Receiver from './components/Receiver';
import Sender from './components/Sender';

interface Params {
    type: GiftItemType;
    giftItemId: string;
    qty: number;
    giftCardImageId?: string;
    originalRequest?: string;
}

declare module '@stackflow/config' {
    interface Register {
        GiftOrderActivity: Params;
    }
}

function OrderBody({
    type,
    giftItemId,
    initialQty,
    giftCardImageId,
}: {
    type: GiftItemType;
    giftItemId: string;
    initialQty: number;
    giftCardImageId?: string;
}) {
    const isCard = type === 'CASH';

    const giftItem = useGiftItem(giftItemId);
    const { t } = useTranslation('gift');
    const { state } = useContext(GiftOrderContext);
    const [qty, setQty] = useState(initialQty);
    const [agreed, setAgreed] = useState(false);
    const unitTotal = giftItem ? giftItem.salePrice * qty : 0;
    const recipientCount = state.giftRecipients.length;
    const totalAmount = unitTotal * recipientCount;

    if (!giftItem) return null;

    return (
        <CtaLayout
            header={<Header title={t('giftOrder.title')} />}
            footer={(
                <Footer
                    giftItem={giftItem}
                    qty={qty}
                    giftCardImageId={giftCardImageId}
                    agreed={agreed}
                />
            )}>
            <Page>
                {isCard ? (
                    <CardInfo giftItem={giftItem} giftCardImageId={giftCardImageId!} />
                ) : (
                    <ItemInfo giftItem={giftItem} qty={qty} onQtyChange={setQty} />
                )}
                <SectionCard fullWidth spacing={6}>
                    {!isCard && <Message />}
                    <Sender />
                    <Receiver />
                </SectionCard>
                <PaymentSection>
                    <PaymentInfo orderAmount={totalAmount} totalAmount={totalAmount} />
                </PaymentSection>
                <GiftTypeNotes giftItemType={type} />
                <OrderAgreements agreed={agreed} onAgreedChange={setAgreed} />
            </Page>
            <Complete />
        </CtaLayout>
    );
}

const GiftOrderActivity: ActivityComponentType<'GiftOrderActivity'> = ({ params }) => {
    const {
        type, giftItemId, qty, giftCardImageId, originalRequest,
    } = params;

    return (
        <AppScreen>
            <GiftOrderProvider originalRequest={originalRequest}>
                <OrderForm>
                    <OrderBody
                        type={type}
                        giftItemId={giftItemId}
                        initialQty={qty}
                        giftCardImageId={giftCardImageId}
                    />
                </OrderForm>
            </GiftOrderProvider>
        </AppScreen>
    );
};

const Page = styled.div`
    display: flex;
    width: 100%;
    min-height: 100%;
    flex-direction: column;
    background: ${colors.white};
`;

const SectionCard = styled(Column)`
    padding: var(--spacing-4);
    border-top: 8px solid ${colors.gray1};
    background: ${colors.white};
`;

const PaymentSection = styled.div`
    width: 100%;
    padding: var(--spacing-4);
    border-top: 8px solid ${colors.gray1};
`;

const OrderForm = styled.form`
    width: 100%;
    height: 100%;
`;

export default GiftOrderActivity;
